// UserSelection.jsx - Enhanced with smart KYC handling for partial data
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Modal from "../../../components/common/Modal";
import Checkbox from "../../../components/common/Checkbox";
import Badge from "../../../components/common/Badge";
import {
  notifyError,
  notifySuccess,
  notifyInfo,
  notifyWarning,
} from "../../../utils/notification";
import {
  createUserByAdminThunk,
  updateUserByAdminThunk,
  getUsersForAdminThunk,
  clearUserError,
} from "../../../store/thunks/userThunks";
import {
  UserIcon,
  PhoneIcon,
  MailIcon,
  LocationMarkerIcon,
  DocumentTextIcon,
  IdentificationIcon,
  OfficeBuildingIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  SearchIcon,
  PlusCircleIcon,
  UserAddIcon,
  ArrowRightIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  XCircleIcon,
  RefreshIcon,
} from "@heroicons/react/outline";

const UserSelection = ({
  isOpen = false,
  onClose,
  onUserSelected,
  mode = "smart",
  existingUser = null,
  title = "Customer Selection",
  showKYC = false,
  showAddress = false,
  showGSTIN = false,
  showAlternatePhone = false,
  showEmail = true,
  requiredFields = [],
  allowSearch = true,
  allowCreate = true,
  allowEdit = true,
  autoFillFromSearch = true,
}) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const searchInputRef = useRef(null);
  const { users, loading, error } = useSelector((state) => state.user);
  const loadingList = loading;

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    alternativePhoneNumber: "",
    address: {
      label: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      pinCode: "",
    },
    gstin: "",
    accountType: "B2C",
    jewelleryProfile: {
      governmentId: "",
      governmentIdType: "PAN",
      nominee: {
        name: "",
        phoneNumber: "",
        relationship: "",
      },
      kycVerified: false,
    },
  });

  const [formErrors, setFormErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [currentMode, setCurrentMode] = useState(
    mode === "smart" ? "search" : mode
  );
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    contact: false,
    address: false,
    business: false,
    kyc: false,
  });
  const [searchSuggestion, setSearchSuggestion] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [kycAutoFilled, setKycAutoFilled] = useState(false);
  const [kycPartialData, setKycPartialData] = useState({
    hasPartialId: false,
    partialIdValue: "",
    isIdMasked: false,
  });
  const searchTimeoutRef = useRef(null);

  // Initialize form data and mode
  useEffect(() => {
    if (isOpen) {
      if (mode === "smart") {
        setCurrentMode("search");
      } else {
        setCurrentMode(mode);
      }

      if (existingUser && typeof existingUser === "object" && mode === "edit") {
        populateFormData(existingUser);
        setSelectedUserId(existingUser._id || existingUser.customerId);
      } else if (mode === "create" || mode === "smart") {
        resetForm();
      }

      setFormErrors({});
      setHasSearched(false);
      setKycAutoFilled(false);
      setKycPartialData({
        hasPartialId: false,
        partialIdValue: "",
        isIdMasked: false,
      });

      setTimeout(() => {
        if (searchInputRef.current && (mode === "smart" || mode === "select")) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, existingUser, mode]);

  // Clear errors
  useEffect(() => {
    if (error) {
      notifyError(error);
      dispatch(clearUserError());
    }
  }, [error, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Parse search query for smart detection
  const parseSearchQuery = (query) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const gstinRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

    const suggestion = {
      type: null,
      value: query,
      fields: {},
    };

    if (phoneRegex.test(query.replace(/\D/g, ""))) {
      suggestion.type = "phone";
      suggestion.fields.phoneNumber = query.replace(/\D/g, "");
    } else if (emailRegex.test(query)) {
      suggestion.type = "email";
      suggestion.fields.email = query.toLowerCase();
    } else if (gstinRegex.test(query.toUpperCase())) {
      suggestion.type = "gstin";
      suggestion.fields.gstin = query.toUpperCase();
    } else if (query.length > 0) {
      suggestion.type = "name";
      suggestion.fields.name = query;
    }

    return suggestion;
  };

  // Check if government ID is partial/masked
  const isPartialGovernmentId = (id, type) => {
    if (!id) return false;

    // Check if it's only last 4 digits (less than full length)
    if (type === "PAN" && id.length < 10) {
      return true;
    } else if (type === "AADHAAR" && id.length < 12) {
      return true;
    }

    // Check if it contains masking characters like 'X', '*', or '-'
    if (/[X*-]/.test(id)) {
      return true;
    }

    return false;
  };

  // Enhanced populate form data with smart KYC handling
  const populateFormData = (user) => {
    if (!user || typeof user !== "object") {
      resetForm();
      return;
    }

    // Extract KYC data with fallbacks
    const jewelleryProfile = user.jewelleryProfile || {};
    const nominee = jewelleryProfile.nominee || {};

    // Smart government ID handling
    let governmentId = "";
    let governmentIdType = "PAN";
    let isPartial = false;
    let partialValue = "";

    if (jewelleryProfile.governmentIdLast4) {
      // Only last 4 digits available
      governmentId = jewelleryProfile.governmentIdLast4;
      governmentIdType = jewelleryProfile.governmentIdType || "PAN";
      isPartial = true;
      partialValue = jewelleryProfile.governmentIdLast4;
    } else if (jewelleryProfile.governmentId) {
      governmentId = jewelleryProfile.governmentId;
      governmentIdType = jewelleryProfile.governmentIdType || "PAN";

      // Check if the ID itself is partial/masked
      if (isPartialGovernmentId(governmentId, governmentIdType)) {
        isPartial = true;
        partialValue = governmentId;
      }
    }

    const populatedData = {
      name: user.name || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      alternativePhoneNumber: user.alternativePhoneNumber || "",
      address:
        user.address && typeof user.address === "object"
          ? {
              label: user.address.label || "",
              line1: user.address.line1 || "",
              line2: user.address.line2 || "",
              city: user.address.city || "",
              state: user.address.state || "",
              pinCode: user.address.pinCode || "",
            }
          : {
              label: "",
              line1: "",
              line2: "",
              city: "",
              state: "",
              pinCode: "",
            },
      gstin: user.gstin || "",
      accountType: user.accountType || "B2C",
      jewelleryProfile: {
        governmentId: isPartial ? "" : governmentId, // Don't auto-fill if partial
        governmentIdType: governmentIdType,
        nominee: {
          name: nominee.name || "",
          phoneNumber: nominee.phoneNumber || "",
          relationship: nominee.relationship || "",
        },
        kycVerified: jewelleryProfile.kycVerified || false,
      },
    };

    setFormData(populatedData);

    // Set partial data state
    setKycPartialData({
      hasPartialId: isPartial,
      partialIdValue: partialValue,
      isIdMasked: isPartial,
    });

    // Check if KYC data was auto-filled
    const hasKycData = !!(
      (!isPartial && governmentId) ||
      nominee.name ||
      nominee.phoneNumber ||
      nominee.relationship
    );

    if (hasKycData || isPartial) {
      setKycAutoFilled(true);
      // Auto-expand KYC section if data is present
      setExpandedSections((prev) => ({
        ...prev,
        kyc: true,
        basic: true,
      }));

      if (isPartial) {
        notifyInfo(
          `KYC data on file (ID: last 4 digits ${partialValue}). Update if needed or proceed as-is.`
        );
      } else {
        notifyInfo("KYC information auto-populated from customer profile");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phoneNumber: "",
      alternativePhoneNumber: "",
      address: {
        label: "",
        line1: "",
        line2: "",
        city: "",
        state: "",
        pinCode: "",
      },
      gstin: "",
      accountType: "B2C",
      jewelleryProfile: {
        governmentId: "",
        governmentIdType: "PAN",
        nominee: {
          name: "",
          phoneNumber: "",
          relationship: "",
        },
        kycVerified: false,
      },
    });
    setSelectedUserId(null);
    setFormErrors({});
    setSearchSuggestion(null);
    setKycAutoFilled(false);
    setKycPartialData({
      hasPartialId: false,
      partialIdValue: "",
      isIdMasked: false,
    });
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    // Clear error for this field
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });

    // If government ID is being changed and we had partial data, clear the partial flag
    if (
      name === "jewelleryProfile.governmentId" &&
      kycPartialData.hasPartialId
    ) {
      setKycPartialData({
        hasPartialId: false,
        partialIdValue: "",
        isIdMasked: false,
      });
    }

    // Handle nested fields
    if (name.includes(".")) {
      const keys = name.split(".");
      setFormData((prev) => {
        const newData = { ...prev };
        let current = newData;

        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = fieldValue;
        return newData;
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: fieldValue }));
    }
  };

  // Handle clearing the partial government ID
  const handleClearPartialId = () => {
    setFormData((prev) => ({
      ...prev,
      jewelleryProfile: {
        ...prev.jewelleryProfile,
        governmentId: "",
      },
    }));
    setKycPartialData({
      hasPartialId: false,
      partialIdValue: "",
      isIdMasked: false,
    });
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors["jewelleryProfile.governmentId"];
      return newErrors;
    });
    notifyInfo("Government ID cleared. Please enter complete ID.");
  };

  // Handle search with debouncing
  const performSearch = useCallback(
    (query) => {
      if (query.length >= 2) {
        setIsSearching(true);
        setHasSearched(true);
        dispatch(getUsersForAdminThunk({ search: query, status: "" })).finally(
          () => setIsSearching(false)
        );
      }
    },
    [dispatch]
  );

  // Handle search input change with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (autoFillFromSearch && query.length > 2) {
      const suggestion = parseSearchQuery(query);
      setSearchSuggestion(suggestion);
    } else {
      setSearchSuggestion(null);
    }

    if (query.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      setHasSearched(false);
      setIsSearching(false);
    }
  };

  // Create user with pre-filled data from search
  const handleCreateFromSearch = () => {
    const newFormData = { ...formData };

    if (searchSuggestion && searchSuggestion.fields) {
      Object.keys(searchSuggestion.fields).forEach((key) => {
        if (key.includes(".")) {
          const keys = key.split(".");
          let current = newFormData;
          for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
              current[keys[i]] = {};
            }
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]] = searchSuggestion.fields[key];
        } else {
          newFormData[key] = searchSuggestion.fields[key];
        }
      });
    }

    setFormData(newFormData);
    setCurrentMode("create");

    const newExpanded = { basic: true };
    if (searchSuggestion?.fields.gstin) newExpanded.business = true;
    setExpandedSections(newExpanded);

    notifyInfo("Creating new customer with pre-filled information");
  };

  // Validation functions
  const validatePhoneNumber = (phone) => {
    return /^[6-9]\d{9}$/.test(phone);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateGSTIN = (gstin) => {
    return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
      gstin
    );
  };

  const validatePinCode = (pinCode) => {
    return /^[1-9][0-9]{5}$/.test(pinCode);
  };

  const validateGovernmentId = (id, type) => {
    if (!id) return true; // Empty is valid if not required

    if (type === "PAN") {
      return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(id);
    } else if (type === "AADHAAR") {
      return /^\d{12}$/.test(id);
    }
    return false;
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.phoneNumber) {
      errors.phoneNumber = "Phone number is required";
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      errors.phoneNumber = "Invalid phone number format";
    }

    if (requiredFields.includes("email")) {
      if (!formData.email) {
        errors.email = "Email is required";
      } else if (!validateEmail(formData.email)) {
        errors.email = "Invalid email format";
      }
    } else if (formData.email && !validateEmail(formData.email)) {
      errors.email = "Invalid email format";
    }

    if (requiredFields.includes("gstin")) {
      if (!formData.gstin) {
        errors.gstin = "GSTIN is required";
      } else if (!validateGSTIN(formData.gstin)) {
        errors.gstin = "Invalid GSTIN format";
      }
    } else if (formData.gstin && !validateGSTIN(formData.gstin)) {
      errors.gstin = "Invalid GSTIN format";
    }

    if (requiredFields.includes("address")) {
      if (!formData.address.line1)
        errors["address.line1"] = "Address Line 1 is required";
      if (!formData.address.city) errors["address.city"] = "City is required";
      if (!formData.address.state)
        errors["address.state"] = "State is required";
      if (!formData.address.pinCode) {
        errors["address.pinCode"] = "PIN code is required";
      } else if (!validatePinCode(formData.address.pinCode)) {
        errors["address.pinCode"] = "Invalid PIN code";
      }
    }

    if (showKYC) {
      // Smart KYC validation logic:
      // 1. If we're in edit mode AND have partial data, KYC is considered complete (don't force update)
      // 2. Only validate if user is actively trying to enter/update data
      // 3. For create mode, follow normal required field rules

      const isEditingWithPartialKYC =
        currentMode === "edit" && kycPartialData.hasPartialId;

      // Government ID validation
      if (requiredFields.includes("governmentId")) {
        // If editing with partial KYC, don't require completion
        if (isEditingWithPartialKYC) {
          // Only validate if user entered something
          if (
            formData.jewelleryProfile.governmentId &&
            !validateGovernmentId(
              formData.jewelleryProfile.governmentId,
              formData.jewelleryProfile.governmentIdType
            )
          ) {
            errors[
              "jewelleryProfile.governmentId"
            ] = `Invalid ${formData.jewelleryProfile.governmentIdType} format`;
          }
          // If empty, it's fine - we have partial data already
        } else {
          // Create mode or edit without partial data - normal validation
          if (!formData.jewelleryProfile.governmentId) {
            errors["jewelleryProfile.governmentId"] =
              "Government ID is required";
          } else if (
            !validateGovernmentId(
              formData.jewelleryProfile.governmentId,
              formData.jewelleryProfile.governmentIdType
            )
          ) {
            errors[
              "jewelleryProfile.governmentId"
            ] = `Invalid ${formData.jewelleryProfile.governmentIdType} format`;
          }
        }
      } else if (
        formData.jewelleryProfile.governmentId &&
        !validateGovernmentId(
          formData.jewelleryProfile.governmentId,
          formData.jewelleryProfile.governmentIdType
        )
      ) {
        // Only validate format if there's a value
        errors[
          "jewelleryProfile.governmentId"
        ] = `Invalid ${formData.jewelleryProfile.governmentIdType} format`;
      }

      // Nominee validation - similar logic
      if (requiredFields.includes("nominee")) {
        // If editing and already has nominee data auto-filled, don't force re-entry
        const hasExistingNominee =
          kycAutoFilled &&
          (formData.jewelleryProfile.nominee.name ||
            formData.jewelleryProfile.nominee.phoneNumber ||
            formData.jewelleryProfile.nominee.relationship);

        if (currentMode === "edit" && hasExistingNominee) {
          // Only validate if fields are being changed
          if (
            formData.jewelleryProfile.nominee.phoneNumber &&
            !validatePhoneNumber(formData.jewelleryProfile.nominee.phoneNumber)
          ) {
            errors["jewelleryProfile.nominee.phoneNumber"] =
              "Invalid phone number";
          }
          // Don't require all fields if some existing data is there
        } else {
          // Normal validation for create or when no existing nominee
          if (!formData.jewelleryProfile.nominee.name) {
            errors["jewelleryProfile.nominee.name"] =
              "Nominee name is required";
          }
          if (!formData.jewelleryProfile.nominee.phoneNumber) {
            errors["jewelleryProfile.nominee.phoneNumber"] =
              "Nominee phone is required";
          } else if (
            !validatePhoneNumber(formData.jewelleryProfile.nominee.phoneNumber)
          ) {
            errors["jewelleryProfile.nominee.phoneNumber"] =
              "Invalid phone number";
          }
          if (!formData.jewelleryProfile.nominee.relationship) {
            errors["jewelleryProfile.nominee.relationship"] =
              "Relationship is required";
          }
        }
      }
    }

    if (
      formData.alternativePhoneNumber &&
      !validatePhoneNumber(formData.alternativePhoneNumber)
    ) {
      errors.alternativePhoneNumber = "Invalid phone number format";
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const newExpanded = { ...expandedSections };
      Object.keys(errors).forEach((key) => {
        if (
          ["name", "phoneNumber", "email", "alternativePhoneNumber"].includes(
            key
          )
        ) {
          newExpanded.basic = true;
        } else if (key.startsWith("address")) {
          newExpanded.address = true;
        } else if (key.startsWith("jewelleryProfile")) {
          newExpanded.kyc = true;
        } else if (key === "gstin") {
          newExpanded.business = true;
        }
      });
      setExpandedSections(newExpanded);
    }

    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      notifyError("Please fix the errors in the form");
      return;
    }

    try {
      let resultUser;

      if (currentMode === "create") {
        const action = await dispatch(createUserByAdminThunk(formData));
        if (createUserByAdminThunk.fulfilled.match(action)) {
          resultUser = action.payload?.user;
          notifySuccess("Customer created successfully");
        } else {
          notifyError(action.payload || "Failed to create customer");
          return;
        }
      } else if (currentMode === "edit" && selectedUserId) {
        const action = await dispatch(
          updateUserByAdminThunk({
            userId: selectedUserId,
            userData: formData,
          })
        );
        if (updateUserByAdminThunk.fulfilled.match(action)) {
          resultUser = action.payload?.user;
          notifySuccess("Customer updated successfully");
        } else {
          notifyError(action.payload || "Failed to update customer");
          return;
        }
      }

      if (onUserSelected && resultUser) {
        onUserSelected(resultUser);
      }

      if (resultUser && resultUser.name) {
        setRecentSearches((prev) => [resultUser.name, ...prev.slice(0, 4)]);
      }

      handleClose();
    } catch (err) {
      notifyError("An error occurred. Please try again.");
    }
  };

  // Handle user selection from search
  const handleSelectUser = (user) => {
    if (!user || typeof user !== "object") return;

    setSelectedUserId(user._id || user.customerId);
    populateFormData(user);

    if (currentMode === "search" && onUserSelected) {
      onUserSelected(user);
      handleClose();
    } else if (allowEdit) {
      setCurrentMode("edit");
      notifyInfo(`Selected ${user.name}. You can now edit their details.`);
    }
  };

  const handleClose = () => {
    resetForm();
    setSearchQuery("");
    setIsSearching(false);
    setHasSearched(false);
    setSearchSuggestion(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    onClose();
  };

  // Filter users based on search
  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return false;
    const query = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.phoneNumber?.includes(searchQuery) ||
      user.gstin?.toLowerCase().includes(query)
    );
  });

  // Render form section
  const renderSection = (sectionName, title, icon, children) => {
    const isExpanded = expandedSections[sectionName];
    const hasErrors = Object.keys(formErrors).some((key) => {
      if (sectionName === "basic")
        return [
          "name",
          "phoneNumber",
          "email",
          "alternativePhoneNumber",
        ].includes(key);
      if (sectionName === "address") return key.startsWith("address");
      if (sectionName === "kyc") return key.startsWith("jewelleryProfile");
      if (sectionName === "business") return key === "gstin";
      return false;
    });

    return (
      <div className="border border-gray-200 rounded-lg mb-4">
        <button
          type="button"
          onClick={() => toggleSection(sectionName)}
          className={`w-full px-4 py-3 flex items-center justify-between ${
            isExpanded ? "bg-gray-50" : "bg-white"
          } hover:bg-gray-50 transition-colors rounded-t-lg`}
        >
          <div className="flex items-center gap-2">
            {React.createElement(icon, {
              className: `w-5 h-5 ${
                hasErrors ? "text-red-500" : "text-gray-600"
              }`,
            })}
            <span
              className={`font-medium ${
                hasErrors ? "text-red-600" : "text-gray-900"
              }`}
            >
              {title}
              {hasErrors && (
                <span className="ml-2 text-xs text-red-500">(Has errors)</span>
              )}
              {sectionName === "kyc" && kycPartialData.hasPartialId && (
                <span className="ml-2 text-xs text-yellow-600">
                  (Partial data)
                </span>
              )}
              {sectionName === "kyc" &&
                kycAutoFilled &&
                !kycPartialData.hasPartialId && (
                  <span className="ml-2 text-xs text-green-600">
                    (Auto-filled)
                  </span>
                )}
            </span>
          </div>
          <ChevronDownIcon
            className={`w-5 h-5 text-gray-400 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>
        {isExpanded && (
          <div className="p-4 border-t border-gray-200">{children}</div>
        )}
      </div>
    );
  };

  // Render search/select interface
  const renderSearchInterface = () => (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search by name, phone, email, or GSTIN..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
          autoFocus
          disabled={loadingList}
        />
        {searchQuery && (
          <button
            onClick={() => {
              setSearchQuery("");
              setSearchSuggestion(null);
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            disabled={loadingList}
          >
            ✕
          </button>
        )}
      </div>

      {/* Smart Suggestion */}
      {searchSuggestion && searchQuery && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <LightBulbIcon className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-blue-800">
                {searchSuggestion.type === "phone" &&
                  `Detected phone number: ${searchSuggestion.fields.phoneNumber}`}
                {searchSuggestion.type === "email" &&
                  `Detected email: ${searchSuggestion.fields.email}`}
                {searchSuggestion.type === "gstin" &&
                  `Detected GSTIN: ${searchSuggestion.fields.gstin}`}
                {searchSuggestion.type === "name" &&
                  `Searching for name: ${searchSuggestion.fields.name}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchQuery && (
        <div>
          {loadingList ? (
            <div className="mt-4 text-center py-8">
              <div className="inline-flex items-center gap-2 text-gray-600">
                <svg
                  className="animate-spin h-5 w-5 text-primary"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                <span>Searching customers...</span>
              </div>
            </div>
          ) : filteredUsers.length > 0 ? (
            <>
              <p className="text-sm text-gray-600 mb-2">
                Found {filteredUsers.length} customer
                {filteredUsers.length !== 1 ? "s" : ""}
              </p>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {filteredUsers.map((user) => {
                  const hasKycInfo =
                    user.jewelleryProfile &&
                    (user.jewelleryProfile.governmentIdLast4 ||
                      user.jewelleryProfile.governmentId ||
                      user.jewelleryProfile.nominee?.name);

                  return (
                    <div
                      key={user._id || user.customerId || Math.random()}
                      onClick={() => handleSelectUser(user)}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedUserId === (user._id || user.customerId)
                          ? "border-primary bg-primary-light"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">
                              {user.name || "Unknown"}
                            </p>
                            {user.status === "accepted" && (
                              <Badge variant="success" size="sm">
                                Active
                              </Badge>
                            )}
                            {hasKycInfo && (
                              <Badge variant="info" size="sm">
                                <ShieldCheckIcon className="w-3 h-3 mr-1 inline" />
                                KYC
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-sm text-gray-600 flex items-center gap-1">
                              <PhoneIcon className="w-3 h-3" />
                              {user.phoneNumber || "No phone"}
                            </span>
                            {user.email && (
                              <span className="text-sm text-gray-600 flex items-center gap-1">
                                <MailIcon className="w-3 h-3" />
                                {user.email}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedUserId === (user._id || user.customerId) && (
                          <CheckCircleIcon className="w-5 h-5 text-primary" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">
                No customers found matching "
                <span className="font-medium">{searchQuery}</span>"
              </p>

              {allowCreate && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Would you like to create a new customer?
                  </p>
                  <Button
                    variant="primary"
                    onClick={handleCreateFromSearch}
                    className="mx-auto"
                  >
                    <UserAddIcon className="w-4 h-4 mr-2" />
                    Create New Customer
                    {searchSuggestion && (
                      <span className="ml-2 text-xs opacity-80">
                        (with {searchSuggestion.type})
                      </span>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!searchQuery && (
        <div className="space-y-3">
          {recentSearches.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Recent:</p>
              <div className="flex flex-wrap gap-2">
                {recentSearches.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => setSearchQuery(search)}
                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    {search}
                  </button>
                ))}
              </div>
            </div>
          )}

          {allowCreate && (
            <button
              onClick={() => setCurrentMode("create")}
              className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-gray-50 transition-all group"
            >
              <div className="flex items-center justify-center gap-3">
                <PlusCircleIcon className="w-6 h-6 text-gray-400 group-hover:text-primary" />
                <span className="text-gray-600 group-hover:text-primary font-medium">
                  Create New Customer Without Search
                </span>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleClose}
      title={
        <div className="flex items-center gap-2">
          {title}
          {currentMode === "create" && searchSuggestion && (
            <Badge variant="info" size="sm">
              Auto-filled from search
            </Badge>
          )}
          {kycPartialData.hasPartialId && currentMode === "edit" && (
            <Badge variant="info" size="sm">
              <ShieldCheckIcon className="w-3 h-3 mr-1 inline" />
              KYC Complete
            </Badge>
          )}
          {kycPartialData.hasPartialId && currentMode === "create" && (
            <Badge variant="warning" size="sm">
              Partial KYC Data
            </Badge>
          )}
          {kycAutoFilled && !kycPartialData.hasPartialId && (
            <Badge variant="success" size="sm">
              <ShieldCheckIcon className="w-3 h-3 mr-1 inline" />
              KYC Auto-populated
            </Badge>
          )}
        </div>
      }
      maxWidth="4xl"
      showConfirmationOnClose={
        currentMode === "create" || currentMode === "edit"
      }
    >
      {/* Mode Indicator */}
      {mode === "smart" && (
        <div className="flex items-center gap-2 mb-4 text-sm">
          <div
            className={`flex items-center gap-2 px-3 py-1 rounded-full ${
              currentMode === "search"
                ? "bg-blue-100 text-blue-700"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            <SearchIcon className="w-4 h-4" />
            Search
          </div>
          {(currentMode === "create" || currentMode === "edit") && (
            <>
              <ArrowRightIcon className="w-4 h-4 text-gray-400" />
              <div
                className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                  currentMode === "create"
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {currentMode === "create" ? (
                  <>
                    <UserAddIcon className="w-4 h-4" />
                    Create
                  </>
                ) : (
                  <>
                    <UserIcon className="w-4 h-4" />
                    Edit
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Search Interface */}
      {(currentMode === "search" || currentMode === "select") &&
        renderSearchInterface()}

      {/* Form Section */}
      {(currentMode === "create" || currentMode === "edit") && (
        <form ref={formRef} onSubmit={handleSubmit}>
          {/* Back to Search Button */}
          {mode === "smart" && allowSearch && (
            <button
              type="button"
              onClick={() => {
                setCurrentMode("search");
                resetForm();
              }}
              className="mb-4 text-sm text-gray-600 hover:text-primary flex items-center gap-1"
            >
              <ArrowRightIcon className="w-3 h-3 rotate-180" />
              Back to search
            </button>
          )}

          {/* Partial KYC Data Info */}
          {kycPartialData.hasPartialId && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    KYC Information On File
                  </p>
                  <p className="text-xs text-blue-700 mb-3">
                    Government ID: Last 4 digits -{" "}
                    <strong>{kycPartialData.partialIdValue}</strong>
                    <br />
                    <em>
                      This customer already has KYC data. You can update it
                      below or leave as-is.
                    </em>
                  </p>
                  <button
                    type="button"
                    onClick={handleClearPartialId}
                    className="text-xs font-medium text-blue-800 hover:text-blue-900 flex items-center gap-1"
                  >
                    <RefreshIcon className="w-3 h-3" />
                    Update government ID
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* KYC Auto-fill Notification */}
          {kycAutoFilled && !kycPartialData.hasPartialId && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2">
                <ShieldCheckIcon className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    KYC Information Auto-populated
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Government ID and nominee details have been loaded from the
                    customer profile
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Check if we have valid data for edit mode */}
          {currentMode === "edit" &&
            !formData.name &&
            !formData.phoneNumber && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  No customer data available for editing. Please select a
                  customer first.
                </p>
                <button
                  type="button"
                  onClick={() => setCurrentMode("search")}
                  className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900"
                >
                  Go to Search →
                </button>
              </div>
            )}

          {/* Basic Information */}
          {renderSection(
            "basic",
            "Basic Information",
            UserIcon,
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                error={formErrors.name}
                placeholder="Enter customer's full name"
              />
              <Input
                label="Phone Number"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                required
                maxLength="10"
                error={formErrors.phoneNumber}
                placeholder="10-digit mobile number"
              />
              {showEmail && (
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required={requiredFields.includes("email")}
                  error={formErrors.email}
                  placeholder="customer@example.com"
                />
              )}
              {showAlternatePhone && (
                <Input
                  label="Alternative Phone"
                  name="alternativePhoneNumber"
                  type="tel"
                  value={formData.alternativePhoneNumber}
                  onChange={handleChange}
                  maxLength="10"
                  error={formErrors.alternativePhoneNumber}
                  placeholder="Optional alternate number"
                />
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Type
                </label>
                <select
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="B2C">B2C - Individual</option>
                  <option value="B2B">B2B - Business</option>
                </select>
              </div>
            </div>
          )}

          {/* Address Section */}
          {showAddress &&
            renderSection(
              "address",
              "Address Details",
              LocationMarkerIcon,
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Input
                    label="Address Label"
                    name="address.label"
                    value={formData.address.label}
                    onChange={handleChange}
                    placeholder="e.g., Home, Office, Shop"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Input
                    label="Address Line 1"
                    name="address.line1"
                    value={formData.address.line1}
                    onChange={handleChange}
                    required={requiredFields.includes("address")}
                    error={formErrors["address.line1"]}
                    placeholder="House/Flat No., Building Name"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Input
                    label="Address Line 2"
                    name="address.line2"
                    value={formData.address.line2}
                    onChange={handleChange}
                    placeholder="Street, Landmark (Optional)"
                  />
                </div>
                <Input
                  label="City"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleChange}
                  required={requiredFields.includes("address")}
                  error={formErrors["address.city"]}
                  placeholder="City name"
                />
                <Input
                  label="State"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleChange}
                  required={requiredFields.includes("address")}
                  error={formErrors["address.state"]}
                  placeholder="State name"
                />
                <Input
                  label="PIN Code"
                  name="address.pinCode"
                  value={formData.address.pinCode}
                  onChange={handleChange}
                  required={requiredFields.includes("address")}
                  maxLength="6"
                  error={formErrors["address.pinCode"]}
                  placeholder="6-digit PIN code"
                />
              </div>
            )}

          {/* Business Information */}
          {showGSTIN &&
            renderSection(
              "business",
              "Business Information",
              OfficeBuildingIcon,
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="GSTIN Number"
                  name="gstin"
                  value={formData.gstin}
                  onChange={handleChange}
                  required={requiredFields.includes("gstin")}
                  maxLength="15"
                  placeholder="22AAAAA0000A1Z5"
                  error={formErrors.gstin}
                />
                <div className="flex items-end">
                  <p className="text-xs text-gray-500">
                    Format: 22AAAAA0000A1Z5 (15 characters)
                  </p>
                </div>
              </div>
            )}

          {/* KYC Section */}
          {showKYC &&
            renderSection(
              "kyc",
              "KYC Information",
              IdentificationIcon,
              <div className="space-y-4">
                {/* KYC Status Banner */}
                {formData.jewelleryProfile.kycVerified && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        KYC Verified Customer
                      </span>
                    </div>
                  </div>
                )}

                {/* Government ID */}
                <div className="border-b pb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Government Identification
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ID Type{" "}
                        {requiredFields.includes("governmentId") && (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      <select
                        name="jewelleryProfile.governmentIdType"
                        value={formData.jewelleryProfile.governmentIdType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="PAN">PAN Card</option>
                        <option value="AADHAAR">Aadhaar Card</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="relative">
                        <Input
                          label={`${formData.jewelleryProfile.governmentIdType} Number`}
                          name="jewelleryProfile.governmentId"
                          value={formData.jewelleryProfile.governmentId}
                          onChange={handleChange}
                          required={requiredFields.includes("governmentId")}
                          placeholder={
                            kycPartialData.hasPartialId
                              ? `Enter complete ${formData.jewelleryProfile.governmentIdType} (currently: last 4 digits only)`
                              : formData.jewelleryProfile.governmentIdType ===
                                "PAN"
                              ? "ABCDE1234F"
                              : "123456789012"
                          }
                          maxLength={
                            formData.jewelleryProfile.governmentIdType === "PAN"
                              ? "10"
                              : "12"
                          }
                          error={formErrors["jewelleryProfile.governmentId"]}
                        />
                        {kycPartialData.hasPartialId && (
                          <button
                            type="button"
                            onClick={handleClearPartialId}
                            className="absolute right-2 top-9 text-gray-400 hover:text-gray-600"
                            title="Clear partial ID"
                          >
                            <XCircleIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                      {kycPartialData.hasPartialId && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                          <p className="text-blue-800">
                            ℹ️ <strong>On File:</strong> Last 4 digits -{" "}
                            {kycPartialData.partialIdValue}
                            <br />
                            <em>
                              Leave empty to keep existing, or enter complete ID
                              to update
                            </em>
                          </p>
                        </div>
                      )}
                      {!kycPartialData.hasPartialId &&
                        kycAutoFilled &&
                        formData.jewelleryProfile.governmentId && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Auto-filled from customer profile
                          </p>
                        )}
                    </div>
                  </div>
                </div>

                {/* Nominee Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Nominee Details
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Nominee Name"
                      name="jewelleryProfile.nominee.name"
                      value={formData.jewelleryProfile.nominee.name}
                      onChange={handleChange}
                      required={requiredFields.includes("nominee")}
                      error={formErrors["jewelleryProfile.nominee.name"]}
                      placeholder="Full name of nominee"
                    />
                    <Input
                      label="Nominee Phone"
                      name="jewelleryProfile.nominee.phoneNumber"
                      type="tel"
                      value={formData.jewelleryProfile.nominee.phoneNumber}
                      onChange={handleChange}
                      required={requiredFields.includes("nominee")}
                      maxLength="10"
                      error={formErrors["jewelleryProfile.nominee.phoneNumber"]}
                      placeholder="10-digit mobile number"
                    />
                    <Input
                      label="Relationship"
                      name="jewelleryProfile.nominee.relationship"
                      value={formData.jewelleryProfile.nominee.relationship}
                      onChange={handleChange}
                      required={requiredFields.includes("nominee")}
                      placeholder="e.g., Spouse, Father, Mother"
                      error={
                        formErrors["jewelleryProfile.nominee.relationship"]
                      }
                    />
                  </div>
                  {kycAutoFilled &&
                    !kycPartialData.hasPartialId &&
                    (formData.jewelleryProfile.nominee.name ||
                      formData.jewelleryProfile.nominee.phoneNumber ||
                      formData.jewelleryProfile.nominee.relationship) && (
                      <p className="text-xs text-green-600 mt-2">
                        ✓ Nominee information auto-filled from customer profile
                      </p>
                    )}
                </div>
              </div>
            )}

          {/* Form Actions */}
          <div className="flex justify-end mt-6 space-x-4">
            <Button variant="secondary" onClick={handleClose} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" isLoading={loading}>
              {currentMode === "edit" ? "Update Customer" : "Create Customer"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default UserSelection;
